import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'wasp/client/auth';
import { isAdministrator } from '../../client/lib/platformRole';
import { getAllLocations, deleteLocation, useQuery, getLocationById, updateLocation, getLocationTypes } from 'wasp/client/operations';
import { type Location } from 'wasp/entities';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Filter, Plus, MapPin, Users, FileText, Calendar, Building2, Upload, SlidersHorizontal } from 'lucide-react';
import LoadingSpinner from '../../admin/layout/LoadingSpinner';
import { EnhancedActionButton } from '../../components/ui/enhanced-page-layout';
import { TableFilterLayout } from '../../components/ui/table-filter-layout';
import { AdminFlyoutPane } from '../../admin/components/AdminFlyoutPane';
import { FlyoutGridLayout, FlyoutGridSection, FlyoutGridRow } from '../../admin/components/FlyoutGridLayout';
import { toast } from '../../hooks/use-toast';
import { BulkUploadDialog } from '../../admin/bulk-upload/components/BulkUploadDialog';
import { locationConfig } from '../../admin/bulk-upload/configs';
import { useBulkUploadEnabled } from '../../admin/hooks/useBulkUploadEnabled';
import { CANADIAN_PROVINCE_OPTIONS, COUNTRY_CANADA } from './canadianProvinces';

const AdminLocationsPage = () => {
  const { data: user } = useAuth();
  const { isEnabled: isBulkUploadEnabled } = useBulkUploadEnabled();

  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isPublicFilter, setIsPublicFilter] = useState<string>('all');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [flyoutMode, setFlyoutMode] = useState<'view' | 'edit'>('view');

  const [editFormData, setEditFormData] = useState({
    name: '',
    province: '' as string,
    country: COUNTRY_CANADA,
    locationTypeId: null as string | null,
    isPublic: false,
    allowPublicSubscription: true,
    defaultVisibility: 'VERIFIED',
    timezone: 'UTC',
    isHub: false,
    aggregateChildContent: true,
    parentLocationId: '' as string | null
  });

  const { data, isLoading, error, refetch } = useQuery(getAllLocations, {
    page: currentPage,
    limit: 10,
    search: search || undefined,
    type: typeFilter === 'all' ? undefined : typeFilter,
    isPublic: isPublicFilter === 'all' ? undefined : isPublicFilter === 'true'
  });

  const { data: locationTypes = [] } = useQuery(getLocationTypes);

  const { data: selectedLocation, isLoading: loadingLocation } = useQuery(
    getLocationById,
    { id: selectedLocationId || '' },
    { enabled: !!selectedLocationId, keepPreviousData: true }
  );

  const locationsData = data as { locations: any[]; total: number; page: number; totalPages: number } | undefined;

  useEffect(() => {
    if (selectedLocation) {
      const loc = selectedLocation as any;
      setEditFormData({
        name: loc.name || '',
        province: loc.province || '',
        country: loc.country || COUNTRY_CANADA,
        locationTypeId: loc.locationTypeId || null,
        isPublic: loc.isPublic || false,
        allowPublicSubscription: loc.allowPublicSubscription ?? true,
        defaultVisibility: loc.defaultVisibility || 'VERIFIED',
        timezone: loc.timezone || 'UTC',
        isHub: loc.isHub || false,
        aggregateChildContent: loc.aggregateChildContent ?? true,
        parentLocationId: loc.parentLocationId || null
      });
    }
  }, [selectedLocation]);

  const locationData = selectedLocation as any;
  const hasChanges = locationData ? (
    editFormData.name !== (locationData.name || '') ||
    editFormData.province !== (locationData.province || '') ||
    editFormData.locationTypeId !== (locationData.locationTypeId || null) ||
    editFormData.isPublic !== (locationData.isPublic || false) ||
    editFormData.allowPublicSubscription !== (locationData.allowPublicSubscription ?? true) ||
    editFormData.defaultVisibility !== (locationData.defaultVisibility || 'VERIFIED') ||
    editFormData.timezone !== (locationData.timezone || 'UTC') ||
    editFormData.isHub !== (locationData.isHub || false) ||
    editFormData.aggregateChildContent !== (locationData.aggregateChildContent ?? true) ||
    editFormData.parentLocationId !== (locationData.parentLocationId || null)
  ) : false;

  const availableParentLocations = locationsData?.locations?.filter((loc: any) =>
    loc.id !== selectedLocationId && loc.isHub && loc.parentLocationId !== selectedLocationId
  ) || [];

  const handleLocationClick = (locationId: string) => {
    setSelectedLocationId(locationId);
    setFlyoutMode('view');
  };

  const handleSave = async () => {
    if (!locationData) return;
    if (!editFormData.province) {
      toast({ title: 'Error', description: 'Province is required', variant: 'destructive' });
      return;
    }
    try {
      await updateLocation({ id: locationData.id, ...editFormData });
      await refetch();
      setFlyoutMode('view');
      toast({ title: 'Success', description: 'Location updated successfully' });
    } catch (error: unknown) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Failed to update location', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!locationData) return;
    if (confirm('Are you sure you want to delete this location? This action cannot be undone.')) {
      try {
        await deleteLocation({ id: locationData.id });
        setSelectedLocationId(null);
        await refetch();
        toast({ title: 'Success', description: 'Location deleted successfully' });
      } catch (error: unknown) {
        toast({ title: 'Error', description: 'Failed to delete location: ' + (error instanceof Error ? error.message : 'Unknown error'), variant: 'destructive' });
      }
    }
  };

  const currentLocationIndex = locationsData?.locations?.findIndex(l => l.id === selectedLocationId) ?? -1;
  const hasPrevious = currentLocationIndex > 0;
  const hasNext = currentLocationIndex >= 0 && currentLocationIndex < (locationsData?.locations?.length ?? 0) - 1;

  const handleNavigatePrevious = () => {
    if (hasPrevious && locationsData?.locations) setSelectedLocationId(locationsData.locations[currentLocationIndex - 1].id);
  };
  const handleNavigateNext = () => {
    if (hasNext && locationsData?.locations) setSelectedLocationId(locationsData.locations[currentLocationIndex + 1].id);
  };

  const getLocationTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      APARTMENT_BUILDING: 'text-blue-600', INNOVATION_HUB: 'text-green-600',
      COWORKING_SPACE: 'text-purple-600', SHOPPING_CENTER: 'text-orange-600',
      CAMPUS: 'text-indigo-600', COMMUNITY_CENTER: 'text-pink-600',
      MIXED_USE: 'text-gray-600', OTHER: 'text-slate-600'
    };
    return colors[type] || colors.OTHER;
  };

  const activeFilterCount = [
    search ? 1 : 0,
    typeFilter !== 'all' ? 1 : 0,
    isPublicFilter !== 'all' ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  if (!isAdministrator(user)) {
    return <div>Access denied. Admin privileges required.</div>;
  }

  // Shared filter inputs (used in both inline and sheet)
  const filterInputs = (
    <div className="flex flex-col gap-3">
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Search</label>
        <input
          type="text"
          placeholder="Name, code, or address…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Type</label>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="appearance-none w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All types</option>
          <option value="APARTMENT_BUILDING">Apartment Building</option>
          <option value="INNOVATION_HUB">Innovation Hub</option>
          <option value="COWORKING_SPACE">Coworking Space</option>
          <option value="SHOPPING_CENTER">Shopping Center</option>
          <option value="CAMPUS">Campus</option>
          <option value="COMMUNITY_CENTER">Community Center</option>
          <option value="MIXED_USE">Mixed Use</option>
          <option value="OTHER">Other</option>
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Visibility</label>
        <select
          value={isPublicFilter}
          onChange={(e) => setIsPublicFilter(e.target.value)}
          className="appearance-none w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All</option>
          <option value="true">Public</option>
          <option value="false">Private</option>
        </select>
      </div>
      {activeFilterCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          className="w-full mt-1"
          onClick={() => { setSearch(''); setTypeFilter('all'); setIsPublicFilter('all'); }}
        >
          Clear filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="w-full">
      {/* ── Fixed Header ────────────────────────────────────────────── */}
      <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="px-4 py-2.5">
          {/* Row 1: title + desktop actions */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Location Names</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {locationsData?.total || 0} locations
              </p>
            </div>

            {/* Desktop buttons — hidden on mobile */}
            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowFilters(f => !f)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-transparent dark:border-gray-600 dark:text-gray-300'
                }`}
              >
                <Filter className="w-3.5 h-3.5" />
                Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </button>
              {isBulkUploadEnabled && (
                <EnhancedActionButton onClick={() => setShowBulkUpload(true)} icon={Upload} label="Bulk Upload" variant="secondary" />
              )}
              <Link to="/admin/taxonomy/location-names/new">
                <EnhancedActionButton onClick={() => {}} icon={Plus} label="Create Location" />
              </Link>
            </div>
          </div>

          {/* Row 2 (mobile only): action buttons */}
          <div className="flex sm:hidden items-center gap-2 mt-2">
            <button
              onClick={() => setShowFilterSheet(true)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                activeFilterCount > 0
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
            {isBulkUploadEnabled && (
              <button
                onClick={() => setShowBulkUpload(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload
              </button>
            )}
            <Link
              to="/admin/taxonomy/location-names/new"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Create
            </Link>
          </div>
        </div>
      </div>

      {/* Spacer — taller on mobile (2-row header) than desktop (1-row) */}
      <div className="h-[88px] sm:h-[72px]" />

      {/* ── Desktop inline filter row ───────────────────────────────── */}
      <div className="hidden sm:block">
        <TableFilterLayout
          showFilters={showFilters}
          columns={[
            { title: 'Location', span: 4 },
            { title: 'Type', span: 2 },
            { title: 'Visibility', span: 1 },
            { title: 'Code', span: 3 },
            { title: 'Stats', span: 2 },
          ]}
          filters={[
            {
              span: 3,
              content: (
                <input
                  type="text"
                  placeholder="Search name, code, or address…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )
            },
            {
              span: 2,
              content: (
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All types</option>
                  <option value="APARTMENT_BUILDING">Apartment Building</option>
                  <option value="INNOVATION_HUB">Innovation Hub</option>
                  <option value="COWORKING_SPACE">Coworking Space</option>
                  <option value="SHOPPING_CENTER">Shopping Center</option>
                  <option value="CAMPUS">Campus</option>
                  <option value="COMMUNITY_CENTER">Community Center</option>
                  <option value="MIXED_USE">Mixed Use</option>
                  <option value="OTHER">Other</option>
                </select>
              )
            },
            {
              span: 1,
              content: (
                <select
                  value={isPublicFilter}
                  onChange={(e) => setIsPublicFilter(e.target.value)}
                  className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="true">Public</option>
                  <option value="false">Private</option>
                </select>
              )
            },
            { span: 3, content: <div className="h-10" /> },
            { span: 2, content: <div className="h-10" /> },
          ]}
        />
      </div>

      {/* ── Mobile filter Sheet sidebar ─────────────────────────────── */}
      <Sheet open={showFilterSheet} onOpenChange={setShowFilterSheet}>
        <SheetContent side="right" className="w-72 sm:hidden">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            {filterInputs}
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Content ─────────────────────────────────────────────────── */}
      <div className="w-full">
        {isLoading && <LoadingSpinner />}
        {error && <div className="p-4 text-red-600 text-sm">Error: {error instanceof Error ? error.message : 'Unknown error'}</div>}

        {locationsData?.locations && locationsData.locations.length > 0 ? (
          <div className="w-full">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {locationsData.locations.map((location: Location & { _count: any }) => (
                <div
                  key={location.id}
                  className="admin-hoverable transition-colors cursor-pointer"
                  onClick={() => handleLocationClick(location.id)}
                  title="Click to view details"
                >
                  {/* ── Desktop row (sm+) ── */}
                  <div className="hidden sm:grid grid-cols-12 gap-4 items-start px-4 py-2.5">
                    <div className="col-span-4 overflow-hidden min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{location.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {(location as any).province ? (location as any).province.replaceAll('_', ' ') : '—'}, Canada
                      </div>
                      {(location as any).isHub && (location as any)._count?.childLocations > 0 && (
                        <div className="text-xs text-primary mt-0.5 truncate">
                          Hub · {(location as any)._count.childLocations} sub-location{(location as any)._count.childLocations !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className={`text-xs font-medium truncate block ${getLocationTypeColor(location.type)}`}>
                        {location.type.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="col-span-1 overflow-hidden min-w-0">
                      <span className={`text-xs font-medium ${location.isPublic ? 'text-green-600' : 'text-gray-500'}`}>
                        {location.isPublic ? 'Public' : 'Private'}
                      </span>
                    </div>
                    <div className="col-span-3 overflow-hidden min-w-0">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{location.locationCode}</span>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{location._count?.memberships || 0}</span>
                        <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{location._count?.content || 0}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{location._count?.events || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* ── Mobile card ── */}
                  <div className="sm:hidden px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{location.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                          {(location as any).province ? (location as any).province.replaceAll('_', ' ') : '—'}, Canada
                        </div>
                        {(location as any).isHub && (location as any)._count?.childLocations > 0 && (
                          <div className="text-xs text-primary mt-0.5">
                            Hub · {(location as any)._count.childLocations} sub-locations
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <span className={`text-xs font-medium ${location.isPublic ? 'text-green-600' : 'text-gray-500'}`}>
                          {location.isPublic ? 'Public' : 'Private'}
                        </span>
                        <div className="text-xs text-gray-400 mt-0.5 font-mono">{location.locationCode}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`text-xs font-medium ${getLocationTypeColor(location.type)}`}>
                        {location.type.replace(/_/g, ' ')}
                      </span>
                      <span className="text-gray-300 dark:text-gray-600">·</span>
                      <span className="flex items-center gap-1 text-xs text-gray-500"><Users className="w-3 h-3" />{location._count?.memberships || 0}</span>
                      <span className="flex items-center gap-1 text-xs text-gray-500"><FileText className="w-3 h-3" />{location._count?.content || 0}</span>
                      <span className="flex items-center gap-1 text-xs text-gray-500"><Calendar className="w-3 h-3" />{location._count?.events || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {locationsData.totalPages > 1 && (
              <div className="admin-table-footer flex items-center justify-between py-3 border-t px-4">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {((currentPage - 1) * 10) + 1}–{Math.min(currentPage * 10, locationsData.total)} of {locationsData.total}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    Prev
                  </Button>
                  <span className="text-xs text-gray-500">{currentPage} / {locationsData.totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(locationsData.totalPages, p + 1))} disabled={currentPage === locationsData.totalPages}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          !isLoading && (
            <div className="text-center py-12 text-gray-500">
              <MapPin className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No locations found</p>
              <Link to="/admin/taxonomy/location-names/new">
                <Button className="mt-4" size="sm">Create your first location</Button>
              </Link>
            </div>
          )
        )}
      </div>

      {/* ── Detail / Edit Flyout ─────────────────────────────────────── */}
      <AdminFlyoutPane
        open={!!selectedLocationId}
        onOpenChange={(open) => { if (!open) { setSelectedLocationId(null); setFlyoutMode('view'); } }}
        title={flyoutMode === 'view' ? 'View Location' : 'Edit Location'}
        description={locationData?.locationCode || undefined}
        width="2xl"
        mode={flyoutMode}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        headerActions={
          flyoutMode === 'view' ? (
            <>
              <Button variant="destructive" size="sm" onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white w-[90px]">Delete</Button>
              <Button onClick={() => setFlyoutMode('edit')} size="sm" className="w-[90px]">Edit</Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setFlyoutMode('view')} className="w-[90px]">{hasChanges ? 'Cancel' : 'Close'}</Button>
              <Button onClick={handleSave} size="sm" className="w-[90px]" disabled={!hasChanges}>Save</Button>
            </>
          )
        }
      >
        {loadingLocation && !selectedLocation ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : locationData ? (
          <FlyoutGridLayout>
            <FlyoutGridSection title="BASIC INFORMATION">
              <FlyoutGridRow label="Name" value={flyoutMode === 'edit' ? (
                <Input value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1" placeholder="Enter location name" />
              ) : <span>{locationData.name}</span>} />
              <FlyoutGridRow label="Slug" value={
                <div className="flex flex-col">
                  <span className="text-gray-900">{locationData.locationCode}</span>
                  {flyoutMode === 'edit' && <span className="text-xs text-gray-500 mt-1">Slug cannot be changed after creation</span>}
                </div>
              } />
              <FlyoutGridRow label="Province" value={flyoutMode === 'edit' ? (
                <Select value={editFormData.province || 'none'} onValueChange={(v) => setEditFormData({ ...editFormData, province: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"><SelectValue placeholder="Select province" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Select province</SelectItem>
                    {CANADIAN_PROVINCE_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : <span>{locationData.province ? locationData.province.replaceAll('_', ' ') : '—'}</span>} />
              <FlyoutGridRow label="Country" value={<span>Canada</span>} />
              <FlyoutGridRow label="Type" isLast value={flyoutMode === 'edit' ? (
                <Select value={editFormData.locationTypeId || 'none'} onValueChange={(v) => setEditFormData({ ...editFormData, locationTypeId: v === 'none' ? null : v })}>
                  <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"><SelectValue placeholder="— None —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {(locationTypes as any[]).filter((lt: any) => lt.isActive).map((lt: any) => <SelectItem key={lt.id} value={lt.id}>{lt.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : <span>{locationData.locationType?.label || '—'}</span>} />
            </FlyoutGridSection>

            <FlyoutGridSection title="LOCATION SETTINGS">
              <FlyoutGridRow label="Public" value={flyoutMode === 'edit' ? (
                <div className="flex items-center gap-2"><Switch checked={editFormData.isPublic} onCheckedChange={(c) => setEditFormData({ ...editFormData, isPublic: c })} /><span className="text-sm">{editFormData.isPublic ? 'Public' : 'Private'}</span></div>
              ) : <span className={locationData.isPublic ? 'text-green-600 font-medium' : 'text-gray-600'}>{locationData.isPublic ? 'Public' : 'Private'}</span>} />
              <FlyoutGridRow label="Subscriptions" value={flyoutMode === 'edit' ? (
                <div className="flex items-center gap-2"><Switch checked={editFormData.allowPublicSubscription} onCheckedChange={(c) => setEditFormData({ ...editFormData, allowPublicSubscription: c })} /><span className="text-sm">{editFormData.allowPublicSubscription ? 'Allowed' : 'Not Allowed'}</span></div>
              ) : <span>{locationData.allowPublicSubscription ? 'Allowed' : 'Not Allowed'}</span>} />
              <FlyoutGridRow label="Default Visibility" isLast value={flyoutMode === 'edit' ? (
                <Select value={editFormData.defaultVisibility} onValueChange={(v) => setEditFormData({ ...editFormData, defaultVisibility: v })}>
                  <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PUBLIC">Public</SelectItem>
                    <SelectItem value="SUBSCRIBER">Subscribers Only</SelectItem>
                    <SelectItem value="VERIFIED">Verified Members Only</SelectItem>
                  </SelectContent>
                </Select>
              ) : <span>{locationData.defaultVisibility}</span>} />
            </FlyoutGridSection>

            <FlyoutGridSection title="HUB SETTINGS">
              <FlyoutGridRow label="Is Hub" value={flyoutMode === 'edit' ? (
                <div className="flex items-center gap-2"><Switch checked={editFormData.isHub} onCheckedChange={(c) => setEditFormData({ ...editFormData, isHub: c })} /><span className="text-sm">{editFormData.isHub ? 'Yes' : 'No'}</span></div>
              ) : (
                <div className="flex items-center gap-2">
                  {locationData?.isHub ? <><Building2 className="w-4 h-4 text-primary" /><span className="text-primary font-medium">Hub</span></> : <span className="text-gray-600">No</span>}
                </div>
              )} />
              {editFormData.isHub && (
                <FlyoutGridRow label="Aggregate Content" value={flyoutMode === 'edit' ? (
                  <div className="flex items-center gap-2"><Switch checked={editFormData.aggregateChildContent} onCheckedChange={(c) => setEditFormData({ ...editFormData, aggregateChildContent: c })} /><span className="text-sm">{editFormData.aggregateChildContent ? 'Show child content' : 'Hub only'}</span></div>
                ) : <span>{locationData?.aggregateChildContent ? 'Shows all child content' : 'Hub content only'}</span>} />
              )}
              <FlyoutGridRow label="Parent" value={flyoutMode === 'edit' ? (
                <Select value={editFormData.parentLocationId || 'none'} onValueChange={(v) => setEditFormData({ ...editFormData, parentLocationId: v === 'none' ? null : v })}>
                  <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"><SelectValue placeholder="None (Root)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Root)</SelectItem>
                    {availableParentLocations.map((loc: any) => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : <span>{locationData?.parentLocation?.name || 'None (Root)'}</span>} />
              {locationData?.childLocations?.length > 0 && (
                <FlyoutGridRow label="Children" value={
                  <div className="flex flex-wrap gap-1">
                    {locationData.childLocations.map((child: any) => (
                      <span key={child.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">{child.name}</span>
                    ))}
                  </div>
                } />
              )}
              <FlyoutGridRow isLast label="Level" value={<span className="text-sm text-gray-600">Level {locationData?.hierarchyLevel || 0}{locationData?.hierarchyLevel === 0 ? ' (Root)' : ''}</span>} />
            </FlyoutGridSection>

            <FlyoutGridSection title="TIMEZONE">
              <FlyoutGridRow isLast label="Timezone" value={flyoutMode === 'edit' ? (
                <Select value={editFormData.timezone} onValueChange={(v) => setEditFormData({ ...editFormData, timezone: v })}>
                  <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern</SelectItem>
                    <SelectItem value="America/Chicago">Central</SelectItem>
                    <SelectItem value="America/Denver">Mountain</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific</SelectItem>
                    <SelectItem value="Europe/London">London</SelectItem>
                    <SelectItem value="Europe/Paris">Paris</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                    <SelectItem value="Asia/Shanghai">Shanghai</SelectItem>
                  </SelectContent>
                </Select>
              ) : <span>{locationData.timezone}</span>} />
            </FlyoutGridSection>

            {flyoutMode === 'view' && (
              <FlyoutGridSection title="STATISTICS">
                <FlyoutGridRow label="Members" value={<div className="flex items-center gap-2"><Users className="w-4 h-4 text-gray-500" /><span>{(locationData as any)._count?.memberships || 0}</span></div>} />
                <FlyoutGridRow label="Content" value={<div className="flex items-center gap-2"><FileText className="w-4 h-4 text-gray-500" /><span>{(locationData as any)._count?.content || 0}</span></div>} />
                <FlyoutGridRow label="Events" value={<div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-500" /><span>{(locationData as any)._count?.events || 0}</span></div>} />
                <FlyoutGridRow isLast label="Items" value={
                  <Link to={`/admin/taxonomy/location-names/${locationData.id}/items`} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium" onClick={() => setSelectedLocationId(null)}>
                    View Items
                  </Link>
                } />
              </FlyoutGridSection>
            )}
          </FlyoutGridLayout>
        ) : null}
      </AdminFlyoutPane>

      <BulkUploadDialog open={showBulkUpload} onOpenChange={setShowBulkUpload} config={locationConfig} onComplete={() => refetch()} />
    </div>
  );
};

export default AdminLocationsPage;



